import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useToast } from '../context/ToastContext';

interface ItemType { code: string; label: string; per_pile: number; per_pile_folded: number; rent_per_day: string; loss_price: string; }
interface TenantConfig {
  company?: string; currency?: string; vat_rate?: string; item_types?: ItemType[];
  alert_days?: number; window_days?: number; suspicion_days?: number; default_security_days?: number;
  promo_boost?: number; promo_days?: number; vision_enabled?: boolean; vision_daily_limit?: number;
  photo_retention_days?: number; legal_notify_email?: string;
  message_templates?: { low_stock?: string; loss_suspicion?: string; daily_report?: string };
}

export function Config() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [company, setCompany] = useState('');
  const [currency, setCurrency] = useState('');
  const [vat, setVat] = useState('0');
  const [itemTypes, setItemTypes] = useState<ItemType[]>([]);
  const [alertDays, setAlertDays] = useState('');
  const [windowDays, setWindowDays] = useState('');
  const [suspicionDays, setSuspicionDays] = useState('');
  const [securityDays, setSecurityDays] = useState('');
  const [promoBoost, setPromoBoost] = useState('');
  const [promoDays, setPromoDays] = useState('');
  const [visionEnabled, setVisionEnabled] = useState(false);
  const [visionLimit, setVisionLimit] = useState('');
  const [photoRetention, setPhotoRetention] = useState('');
  const [legalNotifyEmail, setLegalNotifyEmail] = useState('');
  const [tplLowStock, setTplLowStock] = useState('');
  const [tplLossSuspicion, setTplLossSuspicion] = useState('');
  const [tplDailyReport, setTplDailyReport] = useState('');
  const [msg, setMsg] = useState<{ text: string; err: boolean } | null>(null);

  async function load() {
    setLoading(true); setError('');
    try {
      const cfg = await api<TenantConfig>('/v1/config');
      setCompany(cfg.company || '');
      setCurrency(cfg.currency || '');
      setVat(cfg.vat_rate ? String(parseFloat(cfg.vat_rate) * 100) : '0');
      setItemTypes(cfg.item_types || []);
      setAlertDays(String(cfg.alert_days ?? ''));
      setWindowDays(String(cfg.window_days ?? ''));
      setSuspicionDays(String(cfg.suspicion_days ?? ''));
      setSecurityDays(String(cfg.default_security_days ?? ''));
      setPromoBoost(String(cfg.promo_boost ?? ''));
      setPromoDays(String(cfg.promo_days ?? ''));
      setVisionEnabled(!!cfg.vision_enabled);
      setVisionLimit(String(cfg.vision_daily_limit ?? ''));
      setPhotoRetention(String(cfg.photo_retention_days ?? ''));
      setLegalNotifyEmail(cfg.legal_notify_email || '');
      const t = cfg.message_templates || {};
      setTplLowStock(t.low_stock || '');
      setTplLossSuspicion(t.loss_suspicion || '');
      setTplDailyReport(t.daily_report || '');
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Erreur de chargement.'); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function addRow() {
    setItemTypes((rows) => [...rows, { code: '', label: '', per_pile: 20, per_pile_folded: 40, rent_per_day: '0', loss_price: '0' }]);
  }
  function updateRow(idx: number, patch: Partial<ItemType>) {
    setItemTypes((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function removeRow(idx: number) {
    setItemTypes((rows) => rows.filter((_, i) => i !== idx));
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    const cleanTypes = itemTypes.filter((it) => it.code.trim()).map((it) => ({
      code: it.code.trim(), label: it.label.trim() || it.code.trim(),
      per_pile: Number(it.per_pile) || 1, per_pile_folded: Number(it.per_pile_folded) || 1,
      rent_per_day: String(parseFloat(it.rent_per_day) || 0),
      loss_price: String(parseFloat(it.loss_price) || 0),
    }));
    if (!cleanTypes.length) { setMsg({ text: 'Au moins un type de caisse est requis.', err: true }); return; }
    const body = {
      company: company.trim(), currency: currency.trim(),
      vat_rate: String((parseFloat(vat) || 0) / 100),
      item_types: cleanTypes,
      alert_days: parseFloat(alertDays), window_days: parseInt(windowDays, 10),
      suspicion_days: parseInt(suspicionDays, 10), default_security_days: parseInt(securityDays, 10),
      promo_boost: parseFloat(promoBoost), promo_days: parseInt(promoDays, 10),
      vision_enabled: visionEnabled, vision_daily_limit: parseInt(visionLimit, 10),
      photo_retention_days: parseInt(photoRetention, 10),
      legal_notify_email: legalNotifyEmail.trim(),
      message_templates: { low_stock: tplLowStock, loss_suspicion: tplLossSuspicion, daily_report: tplDailyReport },
    };
    try {
      await api('/v1/config', { method: 'PATCH', body });
      setMsg({ text: 'Configuration enregistrée.', err: false });
      toast('Configuration enregistrée');
    } catch (e) { setMsg({ text: e instanceof ApiError ? e.message : 'Erreur', err: true }); }
  }

  return (
    <>
      <div className="pagehead">
        <h1>Configuration</h1>
        <div className="actions"><button className="small" onClick={load}>Actualiser</button></div>
      </div>
      {loading && <div className="muted">Chargement...</div>}
      {error && <div className="err">{error}</div>}
      {!loading && !error && (
        <form onSubmit={save}>
          <div className="card">
            <h2>Général</h2>
            <div className="row2">
              <div><label>Nom de la société</label><input value={company} onChange={(e) => setCompany(e.target.value)} /></div>
              <div><label>Devise</label><input maxLength={6} value={currency} onChange={(e) => setCurrency(e.target.value)} /></div>
            </div>
            <label>TVA (%)</label>
            <input type="number" min={0} max={100} step={0.1} value={vat} onChange={(e) => setVat(e.target.value)} />
          </div>

          <div className="card">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <h2 style={{ margin: 0 }}>Types de caisses et tarifs</h2>
              <button type="button" className="small" onClick={addRow}>Ajouter un type</button>
            </div>
            <div className="tblwrap">
              <table><tbody>
                <tr><th>Code</th><th>Libellé</th><th>Unités/pile</th><th>Unités/pile (repliée)</th><th>Location/jour</th><th>Prix perte</th><th></th></tr>
                {itemTypes.map((it, idx) => (
                  <tr key={idx}>
                    <td><input style={{ height: 32 }} value={it.code} onChange={(e) => updateRow(idx, { code: e.target.value })} /></td>
                    <td><input style={{ height: 32 }} value={it.label} onChange={(e) => updateRow(idx, { label: e.target.value })} /></td>
                    <td><input style={{ height: 32 }} type="number" min={1} value={it.per_pile} onChange={(e) => updateRow(idx, { per_pile: Number(e.target.value) })} /></td>
                    <td><input style={{ height: 32 }} type="number" min={1} value={it.per_pile_folded} onChange={(e) => updateRow(idx, { per_pile_folded: Number(e.target.value) })} /></td>
                    <td><input style={{ height: 32 }} type="number" min={0} step={0.01} value={it.rent_per_day} onChange={(e) => updateRow(idx, { rent_per_day: e.target.value })} /></td>
                    <td><input style={{ height: 32 }} type="number" min={0} step={0.01} value={it.loss_price} onChange={(e) => updateRow(idx, { loss_price: e.target.value })} /></td>
                    <td><button type="button" className="small danger-btn" onClick={() => removeRow(idx)}>Retirer</button></td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          </div>

          <div className="card">
            <h2>Stock et alertes</h2>
            <div className="row2">
              <div><label>Alerte sous X jours d'autonomie</label><input type="number" min={0.1} step={0.1} value={alertDays} onChange={(e) => setAlertDays(e.target.value)} /></div>
              <div><label>Fenêtre de calcul (jours)</label><input type="number" min={1} value={windowDays} onChange={(e) => setWindowDays(e.target.value)} /></div>
            </div>
            <div className="row2">
              <div><label>Suspicion de perte après (jours sans décompte)</label><input type="number" min={1} value={suspicionDays} onChange={(e) => setSuspicionDays(e.target.value)} /></div>
              <div><label>Stock de sécurité par défaut (jours)</label><input type="number" min={0} value={securityDays} onChange={(e) => setSecurityDays(e.target.value)} /></div>
            </div>
            <div className="row2">
              <div><label>Majoration en promotion (×)</label><input type="number" min={1} step={0.1} value={promoBoost} onChange={(e) => setPromoBoost(e.target.value)} /></div>
              <div><label>Durée de la promotion (jours)</label><input type="number" min={1} value={promoDays} onChange={(e) => setPromoDays(e.target.value)} /></div>
            </div>
          </div>

          <div className="card">
            <h2>Rappels légaux</h2>
            <label>Email(s) de notification (séparés par des virgules)</label>
            <input value={legalNotifyEmail} onChange={(e) => setLegalNotifyEmail(e.target.value)} placeholder="responsable@exemple.com" />
            <p className="muted" style={{ marginTop: 4 }}>Reçoivent un rappel automatique avant chaque échéance légale configurée dans « Conformité ».</p>
          </div>

          <div className="card">
            <h2>IA de comptage</h2>
            <label className="row" style={{ gap: 8, fontWeight: 'normal', marginTop: '0.4rem' }}>
              <input type="checkbox" style={{ width: 'auto', height: 'auto' }} checked={visionEnabled} onChange={(e) => setVisionEnabled(e.target.checked)} />
              <span>Activer la lecture assistée par IA (photos de camions et bons de livraison)</span>
            </label>
            <div className="row2" style={{ marginTop: '0.6rem' }}>
              <div><label>Plafond d'appels IA / jour</label><input type="number" min={0} value={visionLimit} onChange={(e) => setVisionLimit(e.target.value)} /></div>
              <div><label>Conservation des photos (jours)</label><input type="number" min={30} value={photoRetention} onChange={(e) => setPhotoRetention(e.target.value)} /></div>
            </div>
          </div>

          <div className="card">
            <h2>Modèles de messages WhatsApp</h2>
            <p className="muted">Variables disponibles selon le message : <code>{'{boss}'}</code> <code>{'{stock}'}</code> <code>{'{days}'}</code> <code>{'{company}'}</code> <code>{'{gap}'}</code> <code>{'{date}'}</code> <code>{'{client}'}</code> <code>{'{issued}'}</code> <code>{'{delivered}'}</code></p>
            <label>Stock bas</label>
            <textarea value={tplLowStock} onChange={(e) => setTplLowStock(e.target.value)} />
            <label>Suspicion de perte</label>
            <textarea value={tplLossSuspicion} onChange={(e) => setTplLossSuspicion(e.target.value)} />
            <label>Bilan quotidien</label>
            <textarea value={tplDailyReport} onChange={(e) => setTplDailyReport(e.target.value)} />
          </div>

          <div className="row" style={{ margin: '0.4rem 0 1rem' }}>
            <button type="submit" className="primary">Enregistrer les modifications</button>
          </div>
          {msg && <div className={msg.err ? 'err' : 'ok'}>{msg.text}</div>}
        </form>
      )}
    </>
  );
}
